import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/hooks/useAuth";
import { createLineLinkOneTimePassword } from "../../../api/line";

const buildLineLinkUrl = (oneTimePassword: string): string => {
    const url = new URL(`${process.env.VITE_LIFF_URL}/accountLink/confirm`);
    const params = new URLSearchParams();
    params.set("oneTimePassword", oneTimePassword);
    url.search = params.toString();
    return url.toString();
};

type ShareLineLinkOptions =
    | { url: string; text?: string }
    | { text: string; url?: string };

export const useLineLink = () => {
    const { csrfToken } = useAuth();
    const queryClient = useQueryClient();

    const buildLineLinkUrl = (oneTimePassword: string): string => {
        const url = new URL(`${process.env.VITE_LIFF_URL}/accountLink/confirm`);
        const params = new URLSearchParams();
        params.set("oneTimePassword", oneTimePassword);
        url.search = params.toString();
        return url.toString();
    };

    const mutation = useMutation({
        mutationFn: async (answererId: string): Promise<string> => {
            const oneTimePassword = await createLineLinkOneTimePassword(
                answererId,
                csrfToken
            );
            const url = buildLineLinkUrl(oneTimePassword);
            const cacheKey = ["lineLinkUrl", answererId];
            queryClient.setQueryData(cacheKey, url);
            return url;
        },
    });

    const shareLineLink = (options: ShareLineLinkOptions) => {
        // textのみの場合は social-plugins.line.meでは共有できない。
        // line.me/R/share を使用することで解決するようにしました。
        if (options.text && !options.url) {
            const encodedText = encodeURIComponent(options.text);
            const url = `https://line.me/R/share?text=${encodedText}`;
            window.open(url);
            return;
        }

        const url = new URL("https://social-plugins.line.me/lineit/share");
        const params = new URLSearchParams();
        if (options.url) {
            params.set("url", options.url);
        }
        if (options.text) {
            params.set("text", options.text);
        }
        url.search = params.toString();
        window.open(url.toString());
    };

    return {
        createLineLink: mutation,
        shareLineLink,
    };
};

export const useLineLinkUrl = (answererId: string) => {
    const { csrfToken } = useAuth();
    return useQuery({
        queryKey: ["lineLinkUrl", answererId],
        queryFn: async () => {
            const otp = await createLineLinkOneTimePassword(
                answererId,
                csrfToken
            );
            return buildLineLinkUrl(otp);
        },
        enabled: false, // 自動フェッチを無効化
    });
};
